import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Posição da página ao trocar de rota, num lugar só.
 *
 * O app usa <BrowserRouter> (modo declarativo): nele o React Router não mexe na rolagem, e o
 * <ScrollRestoration> só existe para data routers. A troca de rota é um pushState, então a janela
 * ficava onde a lista estava: quem clicava num card depois de rolar a Home caía no meio ou no fim
 * da página da oferta (comentários, rodapé), sem ver foto, preço e botão.
 *
 * Regra:
 *   - navegação nova (PUSH/REPLACE) para OUTRO pathname: topo, instantâneo, antes do paint;
 *   - voltar/avançar (POP): volta à posição em que aquela entrada do histórico estava;
 *   - mesmo pathname (filtro, busca, ?page=) ou link com #âncora: nada.
 *
 * Por que a restauração é nossa: o Chrome grava a posição da entrada anterior depois do pushState,
 * quando o salto para o topo já aconteceu, e a Home voltava em 0 (medido). Então a posição de cada
 * entrada (location.key) é guardada enquanto se rola, e o navegador fica em modo 'manual'. Como a
 * lista carrega depois de montar, a restauração espera a página ter altura suficiente (até
 * ESPERA_MAX_MS) e desiste se a pessoa rolar antes.
 *
 * Quem rola a vitrine pública é a janela. O /sistema tem contêiner próprio e não é afetado.
 */
export const ESPERA_MAX_MS = 2500;
const posicoes = new Map();

/**
 * O index.css põe `scroll-behavior: smooth` no <html>, e com isso até `behavior: 'auto'` vira
 * animação: a página da oferta encolhe e cresce enquanto carrega e a animação para no meio
 * (medido: terminava em scrollY 901, no fim da página). O salto pede `behavior: 'instant'`, que
 * vence o CSS. Para navegador que não conhece 'instant', o smooth é desligado no <html> durante o
 * salto — com o estilo recalculado ANTES do scrollTo, senão o Chrome ainda usa o smooth antigo
 * (medido) — e devolvido em seguida. Os botões "voltar ao topo" continuam suaves.
 */
export function rolarPara(y) {
    const raiz = document.documentElement;
    const antes = raiz.style.scrollBehavior;
    raiz.style.scrollBehavior = 'auto';
    void window.getComputedStyle(raiz).scrollBehavior;   // força o recálculo antes do salto
    try {
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } catch (_) {
        window.scrollTo(0, y);
    }
    raiz.style.scrollBehavior = antes;
}

export const irParaOTopo = () => rolarPara(0);

function restaurar(y) {
    let parar = false;
    const desistir = () => { parar = true; };
    const eventos = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    eventos.forEach((e) => window.addEventListener(e, desistir, { passive: true, once: true }));
    const limpar = () => eventos.forEach((e) => window.removeEventListener(e, desistir));
    const inicio = Date.now();
    let timer = null;
    const tentar = () => {
        if (parar) return limpar();
        const cabe = document.documentElement.scrollHeight - window.innerHeight >= y;
        if (cabe || Date.now() - inicio >= ESPERA_MAX_MS) {
            rolarPara(y);
            return limpar();
        }
        // setTimeout e não requestAnimationFrame: rAF não roda em aba que não está pintando
        timer = window.setTimeout(tentar, 50);
    };
    tentar();
    return () => { parar = true; window.clearTimeout(timer); limpar(); };
}

export default function RolagemAoNavegar() {
    const { pathname, hash, key } = useLocation();
    const tipo = useNavigationType();
    const anterior = useRef(pathname);
    const chaveAtual = useRef(key);

    useEffect(() => {
        const modo = window.history.scrollRestoration;
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
        const guardar = () => posicoes.set(chaveAtual.current, window.scrollY);
        // no clique também (captura): é o instante exato em que se sai da lista pelo card
        window.addEventListener('scroll', guardar, { passive: true });
        window.addEventListener('click', guardar, true);
        return () => {
            window.removeEventListener('scroll', guardar);
            window.removeEventListener('click', guardar, true);
            if ('scrollRestoration' in window.history) window.history.scrollRestoration = modo;
        };
    }, []);

    useLayoutEffect(() => {
        const mudou = anterior.current !== pathname;
        anterior.current = pathname;
        chaveAtual.current = key;
        if (!mudou || hash) return undefined;
        if (tipo === 'POP') {
            const y = posicoes.get(key);
            if (y > 0) return restaurar(y);
            // sem posição guardada (ex.: a página foi recarregada no meio do caminho): topo
        }
        irParaOTopo();
        return undefined;
    }, [pathname, hash, key, tipo]);

    return null;
}
